import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "@fg/icons";
import { useSaveTask, useTasks, useToggleTask } from "@/api/hooks";
import type { TaskItem } from "@/api/mailApi";
import { pushDesktopNotification } from "@/lib/notifications";
import { formatDay, todayKey, useTaskTimeZone } from "./taskModel";

const FIRED_KEY = "fg:mail:task-reminders-fired";
/** Reminders missed while the app was closed still show if they're this recent. */
const CATCH_UP_MS = 12 * 3_600_000;

/**
 * Task reminders — the same mechanism as the calendar's EventReminders: a
 * desktop notification (Electron bridge or the Web Notification permission
 * granted in Settings › Notifications) plus a card in the reminders corner.
 * Each (task, reminder time) fires once, remembered across reloads and tabs.
 * Rendered inside the shell's shared reminders region.
 */
export function TaskReminders() {
    const tasks = useTasks(false);
    const save = useSaveTask();
    const toggle = useToggleTask();
    const navigate = useNavigate();
    const tz = useTaskTimeZone();
    const [due, setDue] = useState<TaskItem[]>([]);

    useEffect(() => {
        const check = () => {
            const now = Date.now();
            const fired = loadFired();
            const fresh: TaskItem[] = [];
            for (const t of tasks.data ?? []) {
                if (t.isCompleted || !t.reminderAt || t.id.startsWith("tmp-")) continue;
                const at = Date.parse(t.reminderAt);
                if (at > now || at < now - CATCH_UP_MS) continue;
                const key = `${t.id}:${t.reminderAt}`;
                if (fired[key]) continue;
                fired[key] = now;
                fresh.push(t);
            }
            if (fresh.length === 0) return;
            saveFired(fired);
            for (const t of fresh) pushDesktopNotification(t.title, describe(t, tz), { sound: true });
            setDue((prev) => [...prev.filter((p) => !fresh.some((f) => f.id === p.id)), ...fresh].slice(-5));
        };
        check();
        const timer = window.setInterval(check, 30_000);
        return () => window.clearInterval(timer);
    }, [tasks.data, tz]);

    // Completed or deleted elsewhere — drop the card.
    const open = new Set((tasks.data ?? []).map((t) => t.id));
    const cards = due.filter((d) => open.has(d.id));
    const dismiss = (id: string) => setDue((prev) => prev.filter((d) => d.id !== id));
    const current = (id: string) => tasks.data?.find((t) => t.id === id);

    return (
        <>
            {cards.map((d) => (
                <div key={d.id} role="alert" className="pointer-events-auto rounded-lg border bg-card p-3 shadow-lg">
                    <div className="flex items-start gap-2">
                        <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">{d.title}</p>
                            <p className="text-xs text-muted-foreground">{describe(d, tz)}</p>
                        </div>
                        <button type="button" aria-label="Dismiss reminder" onClick={() => dismiss(d.id)} className="rounded p-0.5 hover:bg-accent">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div className="mt-2 flex flex-wrap justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                const t = current(d.id);
                                if (t) save.mutate({ ...t, reminderAt: new Date(Date.now() + 5 * 60_000).toISOString() });
                                dismiss(d.id);
                            }}
                            className="h-7 rounded-md border px-2 text-xs hover:bg-accent"
                        >
                            Snooze 5 min
                        </button>
                        <button
                            type="button"
                            onClick={() => { toggle.mutate({ id: d.id, complete: true }); dismiss(d.id); }}
                            className="h-7 rounded-md border px-2 text-xs hover:bg-accent"
                        >
                            Complete
                        </button>
                        <button
                            type="button"
                            onClick={() => { dismiss(d.id); navigate(`/tasks?task=${encodeURIComponent(d.id)}`); }}
                            className="h-7 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                        >
                            Open
                        </button>
                    </div>
                </div>
            ))}
        </>
    );
}

function describe(t: TaskItem, tz: string): string {
    const today = todayKey(tz);
    return t.dueAt ? `Task due ${formatDay(t.dueAt, tz, today)}` : "Task reminder";
}

function loadFired(): Record<string, number> {
    try {
        return JSON.parse(localStorage.getItem(FIRED_KEY) ?? "{}") as Record<string, number>;
    } catch {
        return {};
    }
}

function saveFired(map: Record<string, number>) {
    const cutoff = Date.now() - 3 * 86_400_000;
    const kept = Object.fromEntries(Object.entries(map).filter(([, t]) => t > cutoff));
    try {
        localStorage.setItem(FIRED_KEY, JSON.stringify(kept));
    } catch { /* storage full or disabled — worst case a reminder repeats */ }
}
